/**
 * App chrome: top nav (primary sections) + left collapsible sidebar (full nav).
 *
 * Topbar keeps the high-frequency sections: Dashboard, Travellers, Trips,
 * Packages, Finance.
 * The sidebar holds full nav (including Tools, Website, Admin & Settings
 * which used to live in the topbar).
 *
 * Layout grid:
 *   [ Sidebar (sticky h-screen)  |  Topbar + Main (scrollable column) ]
 *
 * NOTE: Keep this file lean — no large IIFEs inline. The visual-edits Babel
 * plugin in this repo will crash on deep nested IIFE patterns.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Package, Wallet, ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import UserMenu from './UserMenu';
import NotificationBell from './NotificationBell';
import Sidebar from './Sidebar';
import { isPathAllowed, roleHome } from '@/utils/roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children, user, logout }) => {
  const location = useLocation();
  const p = location.pathname;
  const role = (user?.role || '').toLowerCase();
  const show = (path) => isPathAllowed(role, path);
  const home = roleHome(role);

  const isDashboard = p === '/' || p === '/dashboard';
  const isTravellers = p.startsWith('/clients');
  const isTrips = p.startsWith('/quotes');
  const isPackages = p.startsWith('/itineraries');
  const isAccounting = p.startsWith('/accounting');
  const isInvoices = p.startsWith('/invoices');
  const isPaymentLinks = p.startsWith('/payment-links');
  const isVouchers = p.startsWith('/vouchers');
  const isVendors = p.startsWith('/vendors');
  const isReceipts = p.startsWith('/finance/receipts');
  const financeActive = isAccounting || isInvoices || isPaymentLinks || isVouchers || isVendors || isReceipts;

  const primaryCls = 'group flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-medium transition-colors';
  const activeCls = 'bg-slate-100 text-slate-900';
  const idleCls = 'text-slate-600 hover:bg-slate-50 hover:text-slate-900';
  const dropdownBtnCls = 'flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium transition-colors outline-none';
  const menuItemCls = 'flex items-center w-full px-3 py-2 text-sm rounded cursor-pointer';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30 flex">
      <Sidebar user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <nav className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-30 shadow-sm">
          <div className="px-6 lg:px-8">
            <div className="flex justify-between items-center h-14">
              <Link to={home} className="flex items-center gap-3 shrink-0" data-testid="nav-home">
                <img
                  src={BACKEND_URL + '/api/uploads/travcations-logo.png'}
                  alt="Travcations"
                  className="h-9 w-auto"
                />
              </Link>

              <div className="flex items-center gap-1">
                {show('/') && (
                <Link to="/" data-testid="nav-dashboard" className={'hidden md:flex ' + primaryCls + ' ' + (isDashboard ? activeCls : idleCls)}>
                  <LayoutDashboard className="h-4 w-4 opacity-70" />
                  <span className="hidden md:inline">Dashboard+</span>
                </Link>
                )}
                {show('/clients') && (
                <Link to="/clients" data-testid="nav-travellers" className={'hidden md:flex ' + primaryCls + ' ' + (isTravellers ? activeCls : idleCls)}>
                  <Users className="h-4 w-4 opacity-70" />
                  <span className="hidden md:inline">Travellers</span>
                </Link>
                )}
                {show('/quotes') && (
                <Link to="/quotes" data-testid="nav-trips" className={'hidden md:flex ' + primaryCls + ' ' + (isTrips ? activeCls : idleCls)}>
                  <FileText className="h-4 w-4 opacity-70" />
                  <span className="hidden md:inline">Trips</span>
                </Link>
                )}
                {show('/itineraries') && (
                <Link to="/itineraries" data-testid="nav-packages" className={'hidden md:flex ' + primaryCls + ' ' + (isPackages ? activeCls : idleCls)}>
                  <Package className="h-4 w-4 opacity-70" />
                  <span className="hidden md:inline">Packages</span>
                </Link>
                )}

                {show('/invoices') && (
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid="nav-group-finance" className={'hidden md:flex ' + dropdownBtnCls + ' ' + (financeActive ? activeCls : idleCls)}>
                    <Wallet className="h-4 w-4 opacity-70" />
                    <span>Finance</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px] rounded-lg shadow-lg border-slate-200">
                    {(role === 'admin' || role === 'accounts') && (
                    <DropdownMenuItem asChild>
                      <Link to="/accounting" data-testid="nav-accounting" className={menuItemCls + ' ' + (isAccounting ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Accounting</Link>
                    </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/invoices" data-testid="nav-invoices" className={menuItemCls + ' ' + (isInvoices ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Invoices</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/finance/receipts" data-testid="nav-receipts" className={menuItemCls + ' ' + (isReceipts ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Receipts</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/payment-links" data-testid="nav-payment-links" className={menuItemCls + ' ' + (isPaymentLinks ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Payment Links</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/vouchers" data-testid="nav-vouchers" className={menuItemCls + ' ' + (isVouchers ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Vouchers</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/vendors" data-testid="nav-vendors" className={menuItemCls + ' ' + (isVendors ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700')}>Vendors</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}

                <div className="pl-1 flex items-center gap-1">
                  <NotificationBell />
                  <UserMenu user={user} logout={logout} />
                </div>
              </div>
            </div>
          </div>
        </nav>

        <main className="flex-1 px-6 lg:px-8 py-10 relative z-10">
          <div className="animate-fade-in max-w-7xl mx-auto">{children}</div>
        </main>

        <footer className="py-6 text-center text-xs text-slate-400 border-t border-slate-100 relative z-10">
          <p>Travcations CRM © {new Date().getFullYear()}</p>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
